import React, {ReactNode, Suspense, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import intl from "react-intl-universal";
import {ErrorBoundary} from 'react-error-boundary';
import {useRootStore, useUserState} from "@stores/AppStateContext";

import {SectionMessage} from '../../components/SectionMessage';
import {LoadingCircle} from "../../components/LoadingCircle";
import {Loading} from '../../components/Loading';
import {logErrorToSentry} from '../../../utils/errors/logErrorToSentry';

import PageSurface from "./PageSurface";

const Profile = React.lazy(() => import("../../screens/Profile"));
const WsMembers = React.lazy(() => import("../../screens/WorkspaceSettings/WsMembers"));
const Chartflow = React.lazy(() => import("../../screens/Chartflow"));
const PlansAndBilling = React.lazy(() => import("../../screens/PlansAndBilling"));
const Members = React.lazy(() => import("../../screens/Members"));
const Locations = React.lazy(() => import("../../screens/Locations"));
const Accesses = React.lazy(() => import("../../screens/Accesses"));
const Settings = React.lazy(() => import("../../screens/Settings/Settings"));
const WorkspaceSettings = React.lazy(() => import("../../screens/WorkspaceSettings/WorkspaceSettings"));
const KBLayout = React.lazy(() => import("../../screens/KnowledgeBase"));
const QuizzesBase = React.lazy(() => import("../../screens/Quizzes/QuizzesBase"));
const AcknowledgmentsOverview = React.lazy(() => import("../../screens/AcknowledgmentsOverview"));
const TaskLists = React.lazy(() => import("../../screens/Lists/TaskLists"));
const TaskPerformance = React.lazy(() => import("../../screens/Lists/TaskPerformance"));
const AuditsBase = React.lazy(() => import("../../screens/Audits"));
const Issues = React.lazy(() => import("../../screens/Issues"));
const Finance = React.lazy(() => import("../../screens/Finance").then(m => ({default: m.Finance})));


import {PAGES} from "@stores/ViewStore";


const ScreenErrorFallback = ({screen}: {screen: string}) => (
    <PageSurface>
        <SectionMessage
            className={'PageSurface__width'}
            variant={'error'}
            title={intl.get('screen-error.title').d('Something went wrong')}
            message={intl.get('screen-error.body').d('This section encountered an error. The rest of the app is still available. Reload the page if the problem persists.')}
        />
    </PageSurface>
);

function withScreenBoundary(screen: string, node: ReactNode): ReactNode {
    return (
        <ErrorBoundary
            fallback={<ScreenErrorFallback screen={screen}/>}
            onError={(err) => logErrorToSentry(Object.assign(err, { screen }))}
        >
            {node}
        </ErrorBoundary>
    );
}

export const MainContentRouter = observer(() => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const page = rootStore.view.page;

    const isCurrentUserAdmin = userState?.isCurrentUserAdmin;
    const currentWsId = rootStore.view.workspace?.id;
    const assignmentDS = currentWsId ? userState?.dsManager?.getAuditAssignmentDS(currentWsId) : undefined;
    const isAuditPage = [PAGES.AUDITS, PAGES.AUDIT_VIEW, PAGES.AUDIT_EDIT, PAGES.AUDIT_TAKE, PAGES.AUDIT_LOCATION_RESULT].includes(page as any);

    useEffect(() => {
        if (!isCurrentUserAdmin && assignmentDS && (isAuditPage || page === PAGES.ISSUES) && !assignmentDS.isInitialized) {
            void assignmentDS.initialLoad();
        }
    }, [assignmentDS, isAuditPage, isCurrentUserAdmin, page]);

    // Knowledge Base pages continue to use KnowledgeBase screen
    if ([PAGES.KNOWLEDGE_BASE, PAGES.DOC_EDIT, PAGES.DOC_HISTORY].includes(page as any)) {
        return withScreenBoundary('KnowledgeBase',
            <Suspense fallback={<Loading/>}>
                <KBLayout/>
            </Suspense>
        ) as React.ReactElement;
    }

    let content: ReactNode | null = null;
    const hasAuditAssignments = Boolean(assignmentDS?.allRows.length);

    switch (page) {
        case PAGES.SETTINGS_GENERAL:
            content = withScreenBoundary('Settings',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <Settings/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_MEMBERS:
            content = withScreenBoundary('Members',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <Members/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_LOCATIONS:
            content = withScreenBoundary('Locations',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                        {rootStore.view.workspace ? (
                            <Locations wsId={rootStore.view.workspace!.id}/>
                        ) : (
                            <NoWorkspaceMessage/>
                        )}
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_ACCESSES:
            content = withScreenBoundary('Accesses',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <Accesses/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_BILLING:
            content = withScreenBoundary('PlansAndBilling',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <PlansAndBilling/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.WORKSPACE_SETTINGS_GENERAL:
            content = withScreenBoundary('WorkspaceSettings',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <WorkspaceSettings key={rootStore.view.workspace?.id}/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.WORKSPACE_MEMBERS:
            content = withScreenBoundary('WorkspaceMembers',
                isCurrentUserAdmin ? (
                    <Suspense fallback={<LoadingCircle/>}>
                        <WsMembers key={rootStore.view.workspace?.id}/>
                    </Suspense>
                ) : (
                    <NoAdminAccessRightMessage/>
                )
            );
            break;
        case PAGES.SETTINGS_PROFILE:
            content = withScreenBoundary('Profile',
                <Suspense fallback={<LoadingCircle/>}>
                    <Profile/>
                </Suspense>
            );
            break;
        case PAGES.QUIZZES:
        case PAGES.QUIZ_VIEW:
        case PAGES.QUIZ_EDIT:
        case PAGES.QUIZ_TAKE:
            content = withScreenBoundary('Quizzes',
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    <QuizzesBase/>
                </Suspense>
            );
            break;
        case PAGES.ACKNOWLEDGMENTS_OVERVIEW:
            content = withScreenBoundary('AcknowledgmentsOverview',
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    <AcknowledgmentsOverview/>
                </Suspense>
            );
            break;
        case PAGES.LISTS:
            content = withScreenBoundary('TaskLists',
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    {rootStore.view.workspace ? (
                        <TaskLists wsId={rootStore.view.workspace!.id}/>
                    ) : null}
                </Suspense>
            );
            break;
        case PAGES.TASK_PERFORMANCE:
            content = withScreenBoundary('TaskPerformance',
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    <TaskPerformance/>
                </Suspense>
            );
            break;
        case PAGES.AUDITS:
        case PAGES.AUDIT_VIEW:
        case PAGES.AUDIT_EDIT:
        case PAGES.AUDIT_TAKE:
        case PAGES.AUDIT_LOCATION_RESULT:
            content = withScreenBoundary('Audits',
                !isCurrentUserAdmin && assignmentDS && !assignmentDS.isInitialized ? (
                    <LoadingCircle/>
                ) : !isCurrentUserAdmin && !hasAuditAssignments ? (
                    <NoAdminAccessRightMessage/>
                ) : (
                    <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                        {rootStore.view.workspace ? (
                            <AuditsBase wsId={rootStore.view.workspace!.id}/>
                        ) : null}
                    </Suspense>
                )
            );
            break;
        case PAGES.ISSUES:
            content = withScreenBoundary('Issues',
                <Suspense fallback={<LoadingCircle/>} key={rootStore.view.workspace?.id}>
                    {rootStore.view.workspace ? (
                        <Issues wsId={rootStore.view.workspace!.id}/>
                    ) : null}
                </Suspense>
            );
            break;
        case PAGES.CHARTFLOW:
            content = withScreenBoundary('Chartflow',
                <Suspense fallback={<LoadingCircle/>}>
                    <Chartflow/>
                </Suspense>
            );
            break;
        case PAGES.FINANCE:
            content = withScreenBoundary('Finance',
                <Suspense fallback={<LoadingCircle/>}>
                    <Finance/>
                </Suspense>
            );
            break;
        default:
            content = null;
    }

    return content;
});

const NoWorkspaceMessage = () => (
    <PageSurface>
        <SectionMessage
            className={'PageSurface__width'}
            variant={'info'}
            title={intl.get('locations.no-workspace.title').d('Select a workspace')}
            message={intl.get('locations.no-workspace.body').d('Locations belong to a workspace. Choose or create a workspace to manage its locations.')}
        />
    </PageSurface>
);

const NoAdminAccessRightMessage = () => (
    <PageSurface>
        <SectionMessage
            className={'PageSurface__width'}
            variant={'warning'}
            title={intl.get('settings-menu.no-right_header').d('You do not have access rights')}
            message={intl.get('settings-menu.no-right_body').d('To view or modify this, you need to have Admin or Owner privileges. If access is required, please reach out to the Owner or Admin.')}
        />
    </PageSurface>
);
